// client/src/pages/admin/ManageTemplateFields.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api.js';
import Button from '../../components/ui/Button.js';

// ——— Add Field Modal ——————————————————————————————————
function AddFieldModal({ fieldDefinitions, existingFieldIds, onAdd, onClose, saving }) {
  const [selected, setSelected] = useState('');
  const [label, setLabel] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [isrequired, setIsrequired] = useState(false);

  // Filter out fields already assigned to this template
  const availableFields = fieldDefinitions.filter(
    (fd) => !existingFieldIds.includes(fd.fielddefinitionid)
  );

  const selectedField = fieldDefinitions.find(
    (fd) => fd.fielddefinitionid === parseInt(selected)
  );

  const handleAdd = () => {
    if (!selected) return;
    onAdd({
      fielddefinitionid: parseInt(selected),
      label: label.trim() || null,
      placeholder: placeholder.trim() || null,
      isrequired,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-xl">Add Field</h2>
          <button onClick={onClose} className="text-royal-200 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {availableFields.length === 0 ? (
            <p className="text-royal-200 text-base">All available fields are already assigned to this template.</p>
          ) : (
            <>
              <div>
                <label className="block text-sm text-royal-200 mb-1.5">Field Type *</label>
                <select
                  value={selected}
                  onChange={(e) => {
                    setSelected(e.target.value);
                    const fd = fieldDefinitions.find((f) => f.fielddefinitionid === parseInt(e.target.value));
                    if (fd) {
                      setLabel('');
                      setPlaceholder('');
                    }
                  }}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
                >
                  <option value="">Select a field...</option>
                  <optgroup label="Name Fields">
                    {availableFields.filter((f) => f.fieldtype === 'name').map((fd) => (
                      <option key={fd.fielddefinitionid} value={fd.fielddefinitionid}>
                        {fd.defaultlabel} ({fd.fieldkey})
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Message Fields">
                    {availableFields.filter((f) => f.fieldtype === 'message').map((fd) => (
                      <option key={fd.fielddefinitionid} value={fd.fielddefinitionid}>
                        {fd.defaultlabel} ({fd.fieldkey})
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {selectedField && (
                <>
                  <div className="p-3 bg-royal-800/30 border border-royal-700/50 rounded-lg text-sm">
                    <p className="text-royal-200">
                      <span className="text-royal-200">Key:</span> <code className="text-gold">{selectedField.fieldkey}</code>
                    </p>
                    <p className="text-royal-200">
                      <span className="text-royal-200">Type:</span> {selectedField.inputtype}
                    </p>
                    <p className="text-royal-200">
                      <span className="text-royal-200">Default label:</span> {selectedField.defaultlabel}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-royal-200 mb-1.5">
                      Custom Label <span className="text-royal-200">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder={selectedField.defaultlabel}
                      className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
                    />
                    <p className="text-royal-200 text-xs mt-1">Leave blank to use default</p>
                  </div>

                  <div>
                    <label className="block text-sm text-royal-200 mb-1.5">
                      Custom Placeholder <span className="text-royal-200">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={placeholder}
                      onChange={(e) => setPlaceholder(e.target.value)}
                      placeholder={selectedField.defaultplaceholder || 'No default'}
                      className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isrequired"
                      checked={isrequired}
                      onChange={(e) => setIsrequired(e.target.checked)}
                      className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50"
                    />
                    <label htmlFor="isrequired" className="text-royal-200 text-base">Required field</label>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 pt-0 border-t border-royal-700/50 mt-4">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-200 text-base hover:text-white transition-colors">
            Cancel
          </button>
          {availableFields.length > 0 && (
            <Button onClick={handleAdd} disabled={!selected || saving}>
              {saving ? 'Adding...' : 'Add Field'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ——— Edit Field Modal —————————————————————————————————
function EditFieldModal({ field, onSave, onClose, saving }) {
  const [label, setLabel] = useState(field.label || '');
  const [placeholder, setPlaceholder] = useState(field.placeholder || '');
  const [isrequired, setIsrequired] = useState(field.isrequired || false);
  const [displayorder, setDisplayorder] = useState(field.displayorder || 0);

  const handleSave = () => {
    onSave({
      label: label.trim() || null,
      placeholder: placeholder.trim() || null,
      isrequired,
      displayorder: parseInt(displayorder) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-xl">Edit Field</h2>
          <button onClick={onClose} className="text-royal-200 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-3 bg-royal-800/30 border border-royal-700/50 rounded-lg text-sm">
            <p className="text-royal-200">
              <span className="text-royal-200">Key:</span> <code className="text-gold">{field.fieldkey}</code>
            </p>
            <p className="text-royal-200">
              <span className="text-royal-200">Type:</span> {field.fieldtype} ({field.inputtype})
            </p>
            <p className="text-royal-200">
              <span className="text-royal-200">Default label:</span> {field.defaultlabel}
            </p>
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={field.defaultlabel}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
            <p className="text-royal-200 text-xs mt-1">Leave blank to use default: "{field.defaultlabel}"</p>
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Placeholder</label>
            <input
              type="text"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              placeholder={field.defaultplaceholder || 'No default'}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Display Order</label>
            <input
              type="number"
              value={displayorder}
              onChange={(e) => setDisplayorder(e.target.value)}
              min={0}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
            />
            <p className="text-royal-200 text-xs mt-1">Lower numbers appear first</p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="editIsrequired"
              checked={isrequired}
              onChange={(e) => setIsrequired(e.target.checked)}
              className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50"
            />
            <label htmlFor="editIsrequired" className="text-royal-200 text-base">Required field</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 pt-0 border-t border-royal-700/50 mt-4">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-200 text-base hover:text-white transition-colors">
            Cancel
          </button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ——— Main Component ———————————————————————————————————
export default function ManageTemplateFields() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingField, setEditingField] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templateData, fieldsData, defsData] = await Promise.all([
        api.get(`/admin/templates`).then((d) => d.templates.find((t) => String(t.templateid) === String(templateId))),
        api.get(`/admin/templates/${templateId}/fields`),
        api.get('/admin/template-field-definitions'),
      ]);

      setTemplate(templateData || null);
      setFields(fieldsData.fields || []);
      setFieldDefinitions(defsData.fieldDefinitions || []);
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Failed to load template data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [templateId]);

  const handleAddField = async (fieldData) => {
    setSaving(true);
    setError('');
    try {
      const nextOrder = fields.length > 0 ? Math.max(...fields.map((f) => f.displayorder)) + 1 : 1;
      await api.post(`/admin/templates/${templateId}/fields`, {
        ...fieldData,
        displayorder: nextOrder,
      });
      setShowAddModal(false);
      fetchData();
    } catch (err) {
      setError(err.data?.error || 'Failed to add field');
    }
    setSaving(false);
  };

  const handleUpdateField = async (fieldData) => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/admin/templates/${templateId}/fields/${editingField.templatefieldid}`, fieldData);
      setEditingField(null);
      fetchData();
    } catch (err) {
      setError(err.data?.error || 'Failed to update field');
    }
    setSaving(false);
  };

  const handleDeleteField = async (field) => {
    if (!window.confirm(`Remove "${field.label || field.defaultlabel}" from this template?`)) return;

    setError('');
    try {
      await api.del(`/admin/templates/${templateId}/fields/${field.templatefieldid}`);
      fetchData();
    } catch (err) {
      setError(err.data?.error || 'Failed to remove field');
    }
  };

  const existingFieldIds = fields.map((f) => f.fielddefinitionid);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <p className="text-red-400">Template not found</p>
        <button onClick={() => navigate('/admin/templates')} className="text-gold mt-4">
          ← Back to Templates
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/templates')}
          className="flex items-center gap-2 text-royal-200 hover:text-white transition-colors mb-3"
        >
          <span>←</span>
          <span>Back to Templates</span>
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-2xl text-gold">{template.name}</h1>
            <p className="text-royal-200 text-base">Manage form fields for this template</p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>+ Add Field</Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-base">
          {error}
        </div>
      )}

      {/* Fields List */}
      {fields.length === 0 ? (
        <div className="text-center py-12 bg-royal-800/30 border border-royal-700/50 rounded-xl">
          <p className="text-royal-200 text-base mb-2">No fields assigned to this template</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="text-gold text-base hover:text-gold-light transition-colors"
          >
            Add your first field →
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div
              key={field.templatefieldid}
              className="bg-royal-800/40 border border-royal-700/50 rounded-xl p-4 flex items-center gap-4"
            >
              {/* Order indicator */}
              <div className="w-8 h-8 rounded-full bg-royal-700/50 flex items-center justify-center text-royal-200 text-sm font-medium flex-shrink-0">
                {index + 1}
              </div>

              {/* Field info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    field.fieldtype === 'name' 
                      ? 'bg-blue-900/50 text-blue-300' 
                      : 'bg-purple-900/50 text-purple-300'
                  }`}>
                    {field.fieldtype}
                  </span>
                  <code className="text-gold text-sm">{field.fieldkey}</code>
                  {field.isrequired && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/50 text-red-300">
                      required
                    </span>
                  )}
                </div>
                <p className="text-white text-base font-medium">
                  {field.label || field.defaultlabel}
                </p>
                {(field.placeholder || field.defaultplaceholder) && (
                  <p className="text-royal-200 text-sm truncate">
                    Placeholder: "{field.placeholder || field.defaultplaceholder}"
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditingField(field)}
                  className="p-2 text-royal-200 hover:text-gold rounded-lg hover:bg-royal-700/50 transition-colors"
                  title="Edit"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteField(field)}
                  className="p-2 text-royal-200 hover:text-red-400 rounded-lg hover:bg-royal-700/50 transition-colors"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* HTML Usage Guide */}
      <div className="mt-8 p-4 bg-royal-800/30 border border-royal-700/50 rounded-xl">
        <h3 className="text-gold font-medium text-base mb-2">📝 Using these fields in HTML templates</h3>
        <p className="text-royal-200 text-sm mb-3">
          Access field values in your template HTML using these placeholders:
        </p>
        <div className="bg-royal-900/50 rounded-lg p-3 font-mono text-sm space-y-1">
          <p className="text-royal-200">// In JavaScript:</p>
          <p className="text-green-400">var data = {'{{template_data_json}}'};</p>
          {fields.map((f) => (
            <p key={f.fieldkey} className="text-royal-200">
              var {f.fieldkey.replace(/_/g, '')} = data.{f.fieldkey}; 
              <span className="text-royal-200"> // {f.fieldtype}</span>
            </p>
          ))}
        </div>
      </div>

      {/* Add Field Modal */}
      {showAddModal && (
        <AddFieldModal
          fieldDefinitions={fieldDefinitions}
          existingFieldIds={existingFieldIds}
          onAdd={handleAddField}
          onClose={() => setShowAddModal(false)}
          saving={saving}
        />
      )}

      {/* Edit Field Modal */}
      {editingField && (
        <EditFieldModal
          field={editingField}
          onSave={handleUpdateField}
          onClose={() => setEditingField(null)}
          saving={saving}
        />
      )}
    </div>
  );
}
